import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useOnlineMeeting } from './useOnlineMeeting'
import { BaseButtonLink } from '../../components/button/baseButton'
import Loader from '../../components/loader/Loader'
import Copy from '../../components/icons/copy'
import useTranslation from '../../../utils/translation'
import settings from '../../components/settings'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-end;
  gap: 24px;
`

const Heading = styled.h2`
  margin-top: 0;
  margin-bottom: 0;
`

const Button = styled(BaseButtonLink)`
  && {
    align-self: start;
    margin: 0;
  }
`

const Meeting = styled.div`
  position: relative;
  display: flex;
  gap: 24px;
`

const MeetingLink = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  text-decoration: underline;
  cursor: pointer;
`

const CopyIcon = styled(Copy)`
  fill: none;
`

const Copied = styled.span`
  position: fixed;
  top: ${(p) => p.$y};
  left: ${(p) => p.$x};

  white-space: nowrap;
  font-size: 12px;
  font-weight: 700;
  margin-left: 10px;
  color: #333;
  opacity: ${(p) => (p.$show ? 1 : 0)};
  transform: ${(p) => (p.$show ? 'translateY(-44px)' : 'translateY(-34px)')};
  transition: all 200ms ease;
`

const Error = styled.p`
  margin: 0;
  color: #f00;
  max-width: 400px;
`

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const LoaderCover = styled.div`
  position: absolute;
  left: 0;
  width: 800px;
  height: 200px;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 100000;
  position: relative;
  padding-top: 60px;
`

export const OnlineMeeting = ({ appointmentId, updated, readOnly }) => {
  const { createMeeting, deleteMeeting, meeting, loading } = useOnlineMeeting({
    appointmentId,
    updated,
  })
  const t = useTranslation('app.online-meeting')
  const [position, setPosition] = useState({})
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState()

  const showNoMeeting = useMemo(
    () => !loading && !meeting?.id,
    [meeting, loading]
  )
  const showMeeting = useMemo(
    () => !loading && !!meeting?.id,
    [meeting, loading]
  )

  const copyLink = useCallback(
    (e) => {
      if (!navigator.clipboard) {
        return
      }
      navigator.clipboard.writeText(meeting.joinUrl).then(() => {
        setCopied(true)
        setTimeout(() => {
          setCopied(false)
        }, 1500)
      })
      setPosition({ x: `${e.clientX}px`, y: `${e.clientY}px` })
    },
    [meeting]
  )

  const create = useCallback(() => {
    createMeeting().then((result) => {
      setError(result?.error || '')
    })
  }, [createMeeting])

  if (readOnly && !showMeeting) {
    return <></>
  }

  return (
    <Wrapper>
      <Heading>{t('title')}</Heading>

      {loading && (
        <LoaderContainer>
          <LoaderCover>
            <Loader duration={3} />
          </LoaderCover>
        </LoaderContainer>
      )}
      {!!error && !loading && !showMeeting && <Error>{t('error')}</Error>}
      {showNoMeeting && (
        <>
          <i>{t('no-meeting')}</i>
          <Button size="small" kind="inverted" onClick={create}>
            {t('create-meeting')}
          </Button>
        </>
      )}
      {showMeeting && (
        <>
          <i>{t('created-meeting')}</i>
          <Meeting>
            <Copied $show={copied} $x={position.x} $y={position.y}>
              {t('copied')}
            </Copied>
            <Button
              size="small"
              kind="inverted"
              href={meeting?.joinUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('join-meeting')}
            </Button>
            <MeetingLink onClick={copyLink}>
              <CopyIcon />
              <span>{t('copy-link')}</span>
            </MeetingLink>
            {settings.environment !== 'prod' && (
              <MeetingLink onClick={deleteMeeting}>
                <CopyIcon />
                <span>Delete meeting</span>
              </MeetingLink>
            )}
          </Meeting>
        </>
      )}
    </Wrapper>
  )
}
